import { FaCheckCircle, FaEye, FaEdit, FaTrash, FaPaperPlane, FaPercentage, FaTimesCircle, FaEllipsisH } from "react-icons/fa";
import { XCircle } from "react-bootstrap-icons";
import { Tooltip } from "react-tooltip";

// Composants
import OrderTypeBadge from "../../OrderTypeBadge";
import ValidationStatusBadge from "../../../../ValidationStatusBadge";

// Utilitaires
import { VALIDATION_STATUS } from "../../../../service/utils/validationStatus";
import { formatAmount, formatMarketName, getStatusClass, isOrderEditable, canSubmitToValidation } from "../../../../service/utils/orderUtils";
import { formatAmountInThousands, formatDate } from "../../../../service/utils/formatters";
import { generateReference } from "../../../../service/utils/referenceGenerator";

/**
 * LIGNE DE TABLEAU D'ORDRE - Gère l'affichage et les actions d'une ligne d'ordre
 * @param {Object} props
 * @param {Object} props.order - Données de l'ordre
 * @param {string} props.role - Rôle de l'utilisateur connecté
 * @param {boolean} props.isCurrentUserValidator - Si l'utilisateur est validateur
 * @param {Function} props.onViewOrder - Callback pour voir les détails
 * @param {Function} props.onEditOrder - Callback pour éditer l'ordre
 * @param {Function} props.onDeleteOrder - Callback pour supprimer l'ordre
 * @param {Function} props.onCompleteOrder - Callback pour compléter l'ordre
 * @param {Function} props.onPartialOrder - Callback pour partiel l'ordre
 * @param {Function} props.onCancelOrder - Callback pour annuler l'ordre
 * @param {Function} props.onOpenQuantityModal - Callback pour ouvrir le modal de quantité
 * @param {Function} props.onOpenValidationDetails - Callback pour voir les détails de validation
 * @param {Function} props.onOpenSubmitConfirmation - Callback pour soumettre à validation
 */
const OrderTableRow = ({
  order,
  role,
  isCurrentUserValidator,
  onViewOrder,
  onEditOrder,
  onDeleteOrder,
  onCompleteOrder,
  onPartialOrder,
  onCancelOrder,
  onOpenQuantityModal,
  onOpenValidationDetails,
  onOpenSubmitConfirmation
}) => {
  return (
    <tr key={order.id}>
      {/* Colonne Titres */}
      <td className="py-2">
        <div className="d-flex align-items-center">
            <div className="avatar-sm bg-primary bg-opacity-10 rounded-circle me-3">
              <span className="avatar-title text-black fw-bold">
                {order.securityName?.charAt(0)?.toUpperCase() || 'C'}
              </span>
            </div>
            <div>
              <h6 className="mb-0 fw-semibold">{order.securityName}</h6>
              <small className="text-muted">Ref: {generateReference(order.id)}</small>
            </div>
          </div>
      </td>
      
      {/* Colonne Marché */}
      <td className="py-2">
        {formatMarketName(order.targetMarketName)}
      </td>
      
      {/* Colonnes Destinataire/Émetteur */}
      <td className="py-2">{order.accountName}{order.companyName}</td>
      {/* <td className="py-2">{order.firstName1}{order.companyName1}</td> */}
      
      {/* Colonne Type d'ordre */}
      <td className='text-center'>
        <OrderTypeBadge side={order.side} />
      </td>
      
      {/* Colonne Quantité */}
      <td className="py-2 text-center">{formatAmount(order.quantity)}</td>
      
      {/* Colonne Prix */}
      <td className="py-2"> 
        <span className="counter-amount-badge">
          {formatAmountInThousands(order.price)} 
        </span>
      </td>

      {/* Colonne Statut */}
      <td className="py-2 text-end">
        <span className={`badge rounded-pill fw-medium px-3 py-2 ${getStatusClass(order.status)}`}>
          {order.status}
        </span>
      </td>
      
      {/* Colonne Validation */}
      <td className="py-2 text-center">
        <a
          href="#"
          onClick={(e) => {
            if (order.validationStatut === VALIDATION_STATUS.DRAFT) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            onOpenValidationDetails(order.id, 'Order');
          }}
          data-tooltip-id={`validation-details-tooltip-${order.id}`}
          data-tooltip-content={
            order.validationStatut === VALIDATION_STATUS.DRAFT
              ? "Statut en brouillon — détails indisponibles"
              : "Voir les détails de validation"
          }
          style={{
            pointerEvents: order.validationStatut === VALIDATION_STATUS.DRAFT ? "none" : "auto",
          }}
        >
          <ValidationStatusBadge status={order.validationStatut} />
        </a>
        <Tooltip id={`validation-details-tooltip-${order.id}`} />
      </td>
      <td>
        {formatDate(order.creationDate)}
      </td>
      
      {/* Colonne Actions */}
      <td className="py-2 text-end">
        {/* Bouton Créer transaction pour les ordres partiels Public/Monetary */}
        {(order.targetMarketName === 'Public' || order.targetMarketName === 'Monetary') && order.status === 'Partially' && (
          <button
            className="btn btn-sm btn-primary me-2"
            onClick={() => onOpenQuantityModal(order)}
          >
            Créer transaction
          </button>
        )}
        
        {/* Icône d'échec de transaction */}
        {order.status === 'Echec Completed' && (
          <XCircle className="text-danger me-2" size={20} />
        )}
        
        {/* Menu dropdown d'actions */}
        <div className="d-inline-block">
          <button
            className="btn btn-outline-primary btn-sm rounded p-2 shadow-sm"
            type="button"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            title="Actions"
          >
            <FaEllipsisH />
          </button>

          <div className="dropdown-menu dropdown-menu-end shadow border-0 rounded-2 py-1">
            {/* Actions principales conditionnelles */}
            {order.status !== 'Completed' && order.status !== 'Canceled' && (
              <>
                {/* Actions de statut - uniquement si validé */}
                {order.validationStatut === VALIDATION_STATUS.VALIDATED && (
                  <div className="validation-actions">
                    {/* Compléter */}
                    {!(order.targetMarketName === 'Public' && order.status !== 'Partially') && (
                      <button
                        className="dropdown-item d-flex align-items-center text-success"
                        onClick={() => onCompleteOrder(order.id, order)}
                      >
                        <FaCheckCircle className="me-2" /> Compléter
                      </button>
                    )}

                    {/* Partiel */}
                    {!(order.targetMarketName === 'Public' && order.status === 'Partially') && (
                      <button
                        className="dropdown-item d-flex align-items-center text-warning"
                        onClick={() => onPartialOrder(order.id)}
                      >
                        <FaPercentage className="me-2" /> Partiel
                      </button>
                    )}
                  </div>
                )}

                {/* Annuler - toujours visible si pas completed/canceled */}
                <button
                  className="dropdown-item d-flex align-items-center text-danger"
                  onClick={() => onCancelOrder(order.id)}
                >
                  <FaTimesCircle className="me-2" /> Annuler
                </button>

                {order.validationStatut !== VALIDATION_STATUS.VALIDATED && (
                  <div className="non-validated-state text-muted px-3 py-2 small">
                    {order.validationStatut === VALIDATION_STATUS.DRAFT ? 'Brouillon - ' : 'En attente - '}
                    Validation requise
                  </div>
                )}
              </>
            )}

            <div className="dropdown-divider"></div>

            {/* Actions secondaires (toujours visibles) */}
            <button
              className="dropdown-item d-flex align-items-center text-primary"
              onClick={() => onViewOrder(order.id)}
            >
              <FaEye className="me-2" /> Voir plus
            </button>

            {/* Éditer - seulement si modifiable */}
            {isOrderEditable(order) && (
              <button
                className="dropdown-item d-flex align-items-center text-secondary"
                onClick={() => onEditOrder(order)}
              >
                <FaEdit className="me-2" /> Éditer
              </button>
            )}

            <button
              className="dropdown-item d-flex align-items-center text-danger"
              onClick={() => onDeleteOrder(order)}
            >
              <FaTrash className="me-2" /> Supprimer
            </button>

            {/* Soumettre à validation - seulement si autorisé */}
            {canSubmitToValidation(order, role) && (
              <button
                className="dropdown-item d-flex align-items-center text-primary"
                onClick={() => onOpenSubmitConfirmation(order)}
              >
                <FaPaperPlane className="me-2" /> Soumettre à validation
              </button>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

export default OrderTableRow;